import { Tooltip, TooltipContent, TooltipTrigger } from "@dune/ui";
import { Copy } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { AvailableField } from "./types";

interface AvailableFieldsPanelProps {
	fields: AvailableField[];
	onCopyPlaceholder: (placeholder: string) => void;
}

const calculateMaxLength = (containerWidth: number): number => {
	const availableWidth = containerWidth - 48;
	const maxLength = Math.floor(availableWidth / 8);
	return Math.max(15, Math.min(maxLength, 40));
};

export const AvailableFieldsPanel: React.FC<AvailableFieldsPanelProps> = ({
	fields,
	onCopyPlaceholder,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [maxLength, setMaxLength] = useState(25);

	useEffect(() => {
		const updateMaxLength = () => {
			if (containerRef.current) {
				const width = containerRef.current.offsetWidth;
				setMaxLength(calculateMaxLength(width));
			}
		};

		updateMaxLength();

		const resizeObserver = new ResizeObserver(() => {
			updateMaxLength();
		});

		if (containerRef.current) {
			resizeObserver.observe(containerRef.current);
		}

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	const truncateKey = (key: string): string => {
		if (key.length <= maxLength) return key;
		return `${key.substring(0, maxLength)}...`;
	};

	if (fields.length === 0) {
		return (
			<div className="space-y-4">
				<p className="text-sm text-muted-foreground">
					Cliquez sur un champ pour le copier dans le presse-papiers.
				</p>
				<p className="text-sm text-muted-foreground">
					Aucun champ disponible pour cette entité.
				</p>
			</div>
		);
	}

	const groupedFields = fields.reduce(
		(acc, field) => {
			if (!acc[field.category]) acc[field.category] = [];
			acc[field.category].push(field);
			return acc;
		},
		{} as Record<string, AvailableField[]>,
	);

	return (
		<div ref={containerRef} className="space-y-4">
			<p className="text-sm text-muted-foreground">
				Cliquez sur un champ pour le copier dans le presse-papiers.
			</p>

			<div className="space-y-4">
				{Object.entries(groupedFields).map(([category, categoryFields]) => (
					<div key={category}>
						<h4 className="font-medium text-sm mb-2 text-foreground">
							{category}
						</h4>
						<div className="space-y-1">
							{categoryFields.map((field) => (
								<Tooltip key={field.key}>
									<TooltipTrigger asChild>
										<button
											type="button"
											onClick={() => onCopyPlaceholder(field.key)}
											className="w-full text-left p-2 text-sm hover:bg-muted rounded-md transition-colors group"
										>
											<div className="flex items-center justify-between gap-2">
												<span className="font-mono text-xs text-blue-600 truncate">
													{truncateKey(field.key)}
												</span>
												<Copy className="w-3 h-3 opacity-50 group-hover:opacity-100" />
											</div>
											<div className="text-xs text-muted-foreground mt-1 truncate">
												{field.label}
											</div>
										</button>
									</TooltipTrigger>
									<TooltipContent side="left" className="max-w-xs break-words">
										<div className="space-y-1">
											<div className="font-mono text-xs text-white break-all">
												{`{{${field.key}}}`}
											</div>
											<div className="text-xs break-words">{field.label}</div>
										</div>
									</TooltipContent>
								</Tooltip>
							))}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};
